import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { toast } from 'react-toastify';

import Input from '../components/Input';
import Button from '../components/Button';
import { requestOTP, verifyLoginOTP } from '../services/api';
import { colors } from '../theme';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LoginContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 1rem;
  background-color: #f8f9fa;
  background-image: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
`;

const LoginCard = styled.div`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  width: 100%;
  max-width: 400px;
  animation: ${fadeIn} 0.6s ease-out;
  position: relative;
  overflow: hidden;
  box-sizing: border-box;
  
  @media (max-width: 480px) {
    padding: 1.5rem;
    max-width: 90%;
    margin: 0 auto;
  }
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
    background: linear-gradient(to right, ${colors.darkPurple}, ${colors.softPurple});
  }
`;

const Logo = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  
  img {
    max-width: 140px;
    height: auto;
    
    @media (max-width: 480px) {
      max-width: 120px;
    }
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  
  @media (max-width: 480px) {
    gap: 1.2rem;
  }
`;

const StyledInput = styled(Input)`
  transition: all 0.3s ease;
  width: 100%;
  box-sizing: border-box;
  
  &:focus {
    border-color: ${colors.softPurple};
    box-shadow: 0 0 0 2px rgba(159, 118, 249, 0.2);
  }
`;

const StyledButton = styled(Button)`
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  margin-top: 0.5rem;
  
  @media (max-width: 480px) {
    padding: 0.8rem;
    font-size: 0.9rem;
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 5px;
    height: 5px;
    background: rgba(255, 255, 255, 0.5);
    opacity: 0;
    border-radius: 100%;
    transform: scale(1, 1) translate(-50%);
    transform-origin: 50% 50%;
  }
  
  &:focus:not(:active)::after {
    animation: ripple 1s ease-out;
  }
  
  @keyframes ripple {
    0% {
      transform: scale(0, 0);
      opacity: 0.5;
    }
    20% {
      transform: scale(25, 25);
      opacity: 0.3;
    }
    100% {
      opacity: 0;
      transform: scale(40, 40);
    }
  }
`;

const RegisterLink = styled.div`
  margin-top: 1.5rem;
  text-align: center;
  font-size: 0.9rem;
  color: ${colors.mediumGray};
  
  a {
    color: ${colors.softPurple};
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
  
  @media (max-width: 480px) {
    margin-top: 1.2rem;
    font-size: 0.85rem;
  }
`;

const InfoText = styled.div`
  font-size: 0.9rem;
  color: ${colors.mediumGray};
  text-align: center;
  margin: 0.5rem 0;
  
  @media (max-width: 480px) {
    font-size: 0.8rem;
  }
`;

const ResendLink = styled.span`
  color: ${colors.softPurple};
  margin-left: 0.5rem;
  cursor: pointer;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  color: ${colors.darkPurple};
  font-size: 0.9rem;
  margin-top: 0.5rem;
  padding: 0.5rem;
  background-color: rgba(159, 118, 249, 0.1);
  border-radius: 4px;
  text-align: center;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    phone: '',
    otp: '',
    program_code: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState('phone'); // 'phone' or 'otp'
  const [loginId, setLoginId] = useState(null);
  const [expiryMinutes, setExpiryMinutes] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);
  const [resendCountdown, setResendCountdown] = useState(0);
  const utmParams = {
    utm_source: new URLSearchParams(window.location.search).get('utm_source'),
    utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign')
  };

  const program_code = new URLSearchParams(window.location.search).get('program');

  // Function to generate URL with UTM parameters
  const getUrlWithUtm = (basePath) => {
    const params = new URLSearchParams();
    
    if (utmParams.utm_source) params.append('utm_source', utmParams.utm_source);
    if (utmParams.utm_campaign) params.append('utm_campaign', utmParams.utm_campaign);
    if (program_code) params.append('program', program_code);
    
    return params.toString() ? `${basePath}?${params.toString()}` : basePath;
  };

  const registerUrl = getUrlWithUtm('/register');

  useEffect(() => {
    let timer;
    if (resendCountdown > 0) {
      timer = setTimeout(() => {
        setResendCountdown(prevCount => prevCount - 1);
      }, 1000);
    } else if (resendCountdown === 0) {
      setResendDisabled(false);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [resendCountdown]);

  useEffect(() => {
    // Capture UTM parameters and program from URL on component mount
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmCampaign = urlParams.get('utm_campaign');
    const programCode = urlParams.get('program');
    const phone = urlParams.get('phone');
    
    if (utmSource || utmCampaign) {
      utmParams.utm_source = utmSource || '';
      utmParams.utm_campaign = utmCampaign || '';
    }
    
    const updates = {};
    if (programCode) {
      updates.program_code = programCode;
    }
    if (phone) {
      updates.phone = phone;
    }
    
    if (Object.keys(updates).length > 0) {
      setFormData(prev => ({
        ...prev,
        ...updates
      }));
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone field, only allow digits and limit to 10 characters
    if (name === 'phone') {
      const digitsOnly = value.replace(/\D/g, '');
      setFormData(prev => ({ ...prev, [name]: digitsOnly.slice(0, 10) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    setError('');
  };

  const handlePhoneSubmit = async (e) => {
    e.preventDefault();
    
    // Validate phone number
    if (formData.phone.length !== 10) {
      setError('Phone number must be exactly 10 digits');
      toast.error('Phone number must be exactly 10 digits');
      return;
    }
    
    try {
      setLoading(true);
      setError('');
      
      // Store phone number for verification
      localStorage.setItem('loginAttempt', JSON.stringify({
        phone: formData.phone
      }));
      
      // Request OTP
      const response = await requestOTP(formData.phone);
      
      // Store login ID for OTP verification
      setLoginId(response.login_id);
      setExpiryMinutes(response.expiry_minutes);
      
      // Move to OTP verification step
      setStep('otp');
      
      // Disable resend button and start countdown
      setResendDisabled(true);
      setResendCountdown(120); // 2 minutes in seconds
      
      toast.success('OTP sent successfully!');
    } catch (error) {
      console.error('Login error:', error);
      setError(error.response?.data?.detail || 'Login failed. Please try again.');
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError('');
      
      // Verify OTP
      const userData = await verifyLoginOTP(loginId, formData.otp, formData.program_code);
      
      // Save user data to localStorage
      localStorage.setItem('user', JSON.stringify(userData));
      
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      console.error('OTP verification error:', error);
      setError(error.response?.data?.detail || 'Invalid OTP. Please try again.');
      toast.error(error.response?.data?.detail || 'Invalid OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (resendDisabled) return;
    
    try {
      setLoading(true);
      
      // Request OTP again
      const response = await requestOTP(formData.phone);
      
      // Update login ID and expiry
      setLoginId(response.login_id);
      setExpiryMinutes(response.expiry_minutes);
      
      // Disable resend button and start countdown
      setResendDisabled(true);
      setResendCountdown(120); // 2 minutes in seconds
      
      toast.success('OTP resent successfully!');
    } catch (error) {
      console.error('Resend OTP error:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoginContainer>
      <LoginCard>
        
        {program_code === 'iitj-bsc' ? (
          <Logo><img src="../../assets/iitjodhpur-logo.png" alt="IITJ BSC Logo" width={140} height={60} /></Logo>
        ) : (
          <Logo><img src="../../assets/fut-logo.png" alt="Futurense Logo" width={140} height={60} /></Logo>
        )}
        
        {step === 'phone' ? (
          <Form onSubmit={handlePhoneSubmit}>
            <StyledInput
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Phone Number"
              required
            />
            
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <StyledButton 
              type="submit" 
              disabled={loading}
              fullWidth
            >
              {loading ? 'Processing...' : 'Get OTP'}
            </StyledButton>
          </Form>
        ) : (
          <Form onSubmit={handleOtpSubmit}>
            <InfoText>
              An OTP has been sent to {formData.phone}. 
              It will expire in {expiryMinutes} minutes.
            </InfoText>
            
            <StyledInput
              type="text"
              name="otp"
              value={formData.otp}
              onChange={handleChange}
              placeholder="Enter OTP"
              required
            />
            
            {error && <ErrorMessage>{error}</ErrorMessage>}
            
            <StyledButton 
              type="submit" 
              disabled={loading}
              fullWidth
            >
              {loading ? 'Verifying...' : 'Verify OTP'}
            </StyledButton>
            
            <InfoText>
              Didn't receive the OTP?
              <ResendLink 
                onClick={handleResendOTP} 
                disabled={resendDisabled}
                style={{ 
                  cursor: resendDisabled ? 'not-allowed' : 'pointer',
                  opacity: resendDisabled ? 0.5 : 1 
                }}
              >
                {resendDisabled ? `Resend (${resendCountdown}s)` : 'Resend'}
              </ResendLink>
            </InfoText>
          </Form>
        )}
        
        <RegisterLink>
          Don't have an account? <Link to={registerUrl}>Register</Link>
        </RegisterLink>
      </LoginCard>
    </LoginContainer>
  );
};

export default Login;
